// =============================================================================
// PAGES/DOCUMENTOS.TSX — Documentos no SharePoint (visão Lista OU visão Pastas)
// =============================================================================
// Duas visões para o operador:
//  • "Documentos": lista achatada do banco, com BUSCA (arquivo/operação/cliente)
//    e LINK DIRETO resolvido no clique.
//  • "Pastas": navega a árvore real do SharePoint (tal como ela é) — entra na pasta
//    do cliente e vê todos os documentos dele, com link direto já no listamento.
// =============================================================================
import { useEffect, useState, useCallback, useRef } from 'react';
import {
  FolderOpen, Search, ExternalLink, Loader2, RefreshCw, FileText, Archive,
  Folder, ChevronRight, Home, List,
} from 'lucide-react';
import { toast } from 'sonner';
import { documentosApi, type DocumentoItem, type ConteudoPasta } from '../services/api';

const ORIGEM_LABEL: Record<string, string> = { QUINTO_ANDAR: '5A', S_QUATRO: 'S4', VAREJO: 'VAR' };
const PAGE_SIZE = 25;

function formatarTamanho(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function Documentos() {
  const [modo, setModo] = useState<'documentos' | 'pastas'>('documentos');

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-1">
        <FolderOpen size={22} className="text-otm-orange" />
        <h1 className="text-xl font-bold text-otm-txt-1">Documentos</h1>
      </div>
      <p className="text-sm text-otm-txt-3 mb-4">Arquivos disponíveis no SharePoint. Alterne entre a lista de documentos e a navegação por pastas.</p>

      {/* Alternador de visão */}
      <div className="inline-flex rounded-lg border border-otm-border bg-white p-0.5 mb-4">
        <BotaoModo ativo={modo === 'documentos'} onClick={() => setModo('documentos')} icon={List} label="Documentos" />
        <BotaoModo ativo={modo === 'pastas'} onClick={() => setModo('pastas')} icon={Folder} label="Pastas" />
      </div>

      {modo === 'documentos' ? <VisaoDocumentos /> : <VisaoPastas />}
    </div>
  );
}

function BotaoModo({ ativo, onClick, icon: Icone, label }: { ativo: boolean; onClick: () => void; icon: typeof List; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
        ativo ? 'bg-otm-navy/10 text-otm-navy' : 'text-otm-txt-2 hover:bg-otm-subtle'
      }`}
    >
      <Icone size={16} /> {label}
    </button>
  );
}

// ── Visão "Documentos" (lista achatada do banco, com busca) ───────────────────
function VisaoDocumentos() {
  const [busca, setBusca] = useState('');
  const [apenasPloomes, setApenasPloomes] = useState(false);
  const [page, setPage] = useState(1);
  const [itens, setItens] = useState<DocumentoItem[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [abrindoId, setAbrindoId] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const carregar = useCallback(async (p: number, q: string, soPloomes: boolean) => {
    setCarregando(true);
    try {
      const r = await documentosApi.listar({ busca: q || undefined, apenasPloomes: soPloomes || undefined, page: p, pageSize: PAGE_SIZE });
      setItens(r.itens); setTotal(r.total);
    } catch { toast.error('Falha ao carregar os documentos.'); }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(1); carregar(1, busca, apenasPloomes); }, 350);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [busca, apenasPloomes, carregar]);

  function mudarPagina(p: number) { setPage(p); carregar(p, busca, apenasPloomes); }

  async function abrir(id: string) {
    setAbrindoId(id);
    try {
      const url = await documentosApi.link(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      const msg = (e as { response?: { data?: { mensagem?: string } } })?.response?.data?.mensagem;
      toast.error(msg ?? 'Não foi possível abrir o arquivo no SharePoint.');
    } finally { setAbrindoId(null); }
  }

  const totalPaginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const ini = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const fim = Math.min(page * PAGE_SIZE, total);

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-otm-txt-3" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar documento, operação ou cliente…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-otm-border text-sm text-otm-txt-1 focus:outline-none focus:ring-2 focus:ring-otm-orange/40 focus:border-otm-orange"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-otm-txt-2 select-none cursor-pointer px-1">
          <input type="checkbox" checked={apenasPloomes} onChange={(e) => setApenasPloomes(e.target.checked)} className="rounded border-otm-border text-otm-orange focus:ring-otm-orange" />
          Só histórico Ploomes
        </label>
        <button onClick={() => carregar(page, busca, apenasPloomes)} disabled={carregando} className="inline-flex items-center gap-1.5 text-sm text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-40 px-2" title="Recarregar">
          <RefreshCw size={16} className={carregando ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-otm-border shadow-sm overflow-hidden">
        {carregando && !itens.length ? (
          <div className="flex items-center gap-2 text-otm-txt-3 py-12 justify-center"><Loader2 size={18} className="animate-spin" /> Carregando…</div>
        ) : !itens.length ? (
          <div className="py-12 text-center text-sm text-otm-txt-3">Nenhum documento encontrado.</div>
        ) : (
          <ul className="divide-y divide-otm-border">
            {itens.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-4 py-3 hover:bg-otm-subtle/40">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-otm-subtle flex items-center justify-center text-otm-txt-3">
                  {d.historicoPloomes ? <Archive size={16} /> : <FileText size={16} />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-otm-txt-1 truncate" title={d.nomeArquivo}>{d.nomeArquivo}</p>
                  <p className="text-xs text-otm-txt-3 truncate">
                    {d.origem && <span className="font-semibold">{ORIGEM_LABEL[d.origem] ?? d.origem} · </span>}
                    {d.operacaoNumero ?? '—'}{d.cliente ? ` · ${d.cliente}` : ''}
                    {d.historicoPloomes && <span className="ml-1 inline-block text-[10px] font-semibold text-otm-orange bg-otm-orange/10 rounded-full px-1.5 py-0.5 align-middle">Ploomes</span>}
                  </p>
                </div>
                <button
                  onClick={() => abrir(d.id)}
                  disabled={!d.temLink || abrindoId === d.id}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-otm-border px-3 py-1.5 text-sm text-otm-txt-1 hover:bg-otm-subtle disabled:opacity-40 disabled:cursor-not-allowed"
                  title={d.temLink ? 'Abrir no SharePoint' : 'Sem link disponível'}
                >
                  {abrindoId === d.id ? <Loader2 size={15} className="animate-spin" /> : <ExternalLink size={15} />}
                  Abrir
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between mt-3 text-sm text-otm-txt-3">
          <span>{ini}–{fim} de {total}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => mudarPagina(page - 1)} disabled={page <= 1 || carregando} className="px-3 py-1.5 rounded-lg border border-otm-border hover:bg-otm-subtle disabled:opacity-40">Anterior</button>
            <span className="px-2">{page}/{totalPaginas}</span>
            <button onClick={() => mudarPagina(page + 1)} disabled={page >= totalPaginas || carregando} className="px-3 py-1.5 rounded-lg border border-otm-border hover:bg-otm-subtle disabled:opacity-40">Próxima</button>
          </div>
        </div>
      )}
    </>
  );
}

// ── Visão "Pastas" (navega a árvore real do SharePoint) ───────────────────────
function VisaoPastas() {
  const [caminho, setCaminho] = useState('');          // relativo à raiz (vazio = raiz)
  const [data, setData] = useState<ConteudoPasta | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('');

  const carregar = useCallback(async (c: string) => {
    setCarregando(true); setFiltro('');
    try { setData(await documentosApi.pastas(c || undefined)); }
    catch { toast.error('Falha ao abrir a pasta no SharePoint.'); }
    finally { setCarregando(false); }
  }, []);

  useEffect(() => { carregar(caminho); }, [caminho, carregar]);

  const entrar = (nomePasta: string) => setCaminho((c) => (c ? `${c}/${nomePasta}` : nomePasta));
  const irParaNivel = (idx: number) => {
    if (!data) return;
    setCaminho(idx < 0 ? '' : data.breadcrumb.slice(0, idx + 1).join('/'));
  };

  const q = filtro.trim().toLowerCase();
  const pastas = (data?.pastas ?? []).filter((p) => !q || p.nome.toLowerCase().includes(q));
  const arquivos = (data?.arquivos ?? []).filter((a) => !q || a.nome.toLowerCase().includes(q));
  const vazio = !carregando && data && !pastas.length && !arquivos.length;

  return (
    <>
      {/* Breadcrumb + filtro + recarregar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-1 text-sm flex-wrap flex-1 min-w-0">
          <button onClick={() => irParaNivel(-1)} className="inline-flex items-center gap-1 text-otm-txt-2 hover:text-otm-txt-1" title={data?.base}>
            <Home size={15} /> {data?.base ?? 'Operações'}
          </button>
          {(data?.breadcrumb ?? []).map((seg, i) => (
            <span key={i} className="inline-flex items-center gap-1 min-w-0">
              <ChevronRight size={14} className="text-otm-txt-3 shrink-0" />
              <button
                onClick={() => irParaNivel(i)}
                className={`truncate max-w-[220px] ${i === (data!.breadcrumb.length - 1) ? 'text-otm-txt-1 font-medium' : 'text-otm-txt-2 hover:text-otm-txt-1'}`}
              >
                {seg}
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-otm-txt-3" />
            <input
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Filtrar nesta pasta…"
              className="pl-8 pr-3 py-2 rounded-lg border border-otm-border text-sm w-48 focus:outline-none focus:ring-2 focus:ring-otm-orange/40 focus:border-otm-orange"
            />
          </div>
          <button onClick={() => carregar(caminho)} disabled={carregando} className="text-otm-txt-3 hover:text-otm-txt-1 disabled:opacity-40 px-1" title="Recarregar">
            <RefreshCw size={16} className={carregando ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-otm-border shadow-sm overflow-hidden">
        {carregando && !data ? (
          <div className="flex items-center gap-2 text-otm-txt-3 py-12 justify-center"><Loader2 size={18} className="animate-spin" /> Abrindo pasta…</div>
        ) : vazio ? (
          <div className="py-12 text-center text-sm text-otm-txt-3">Pasta vazia.</div>
        ) : (
          <ul className="divide-y divide-otm-border">
            {pastas.map((p) => (
              <li key={'d' + p.graphItemId}>
                <button onClick={() => entrar(p.nome)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-otm-subtle/40 text-left">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-otm-orange/10 flex items-center justify-center text-otm-orange"><Folder size={16} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-otm-txt-1 truncate" title={p.nome}>{p.nome}</p>
                    {p.qtdItens != null && <p className="text-xs text-otm-txt-3">{p.qtdItens} item(ns)</p>}
                  </div>
                  <ChevronRight size={16} className="text-otm-txt-3 shrink-0" />
                </button>
              </li>
            ))}
            {arquivos.map((a) => (
              <li key={'f' + a.graphItemId} className="flex items-center gap-3 px-4 py-3 hover:bg-otm-subtle/40">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-otm-subtle flex items-center justify-center text-otm-txt-3"><FileText size={16} /></div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-otm-txt-1 truncate" title={a.nome}>{a.nome}</p>
                  <p className="text-xs text-otm-txt-3">{formatarTamanho(a.size)}{a.lastModifiedDateTime ? ` · ${new Date(a.lastModifiedDateTime).toLocaleDateString('pt-BR')}` : ''}</p>
                </div>
                <a
                  href={a.webUrl ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-otm-border px-3 py-1.5 text-sm text-otm-txt-1 hover:bg-otm-subtle ${a.webUrl ? '' : 'opacity-40 pointer-events-none'}`}
                  title="Abrir no SharePoint"
                >
                  <ExternalLink size={15} /> Abrir
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
